import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { signOut } from "../lib/firebase";
import { DirectoryProvider } from "../lib/directory";
import { ToolsNav } from "./ToolsNav";

export default function Layout({ children }: { children: ReactNode }) {
  return (
    <DirectoryProvider>
      <div className="min-h-screen">
        {/* Light topbar, pvt-style: logo + brand left, nav + user chip right. */}
        <header className="border-b border-line bg-white">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-3 sm:gap-4 sm:px-6">
            <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3">
              <img src="/v_transparent.png" alt="" className="h-8 w-8 flex-none object-contain" />
              {/* Wordmark hides on small screens — the mark + favicon carry it. */}
              <span className="hidden text-[15px] font-bold tracking-tight text-ink sm:block">
                Closing Checklists
              </span>
            </Link>
            <div className="flex flex-none items-center gap-2 sm:gap-4">
              <ToolsNav />
              {/* Template moves into the user menu on mobile. */}
              <Link
                to="/template"
                className="hidden rounded-md border border-line px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft transition-colors hover:border-line-strong hover:bg-paper hover:text-ink sm:block"
              >
                View/Edit Template
              </Link>
              <UserChip />
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">{children}</main>
      </div>
    </DirectoryProvider>
  );
}

/**
 * Google-avatar chip with a hover/focus menu (name, email, sign out) —
 * same interaction as pvt's UserChip.
 */
function UserChip() {
  const { user } = useAuth();
  if (!user) return null;
  const firstName = user.displayName?.split(" ")[0] ?? user.email ?? "";
  return (
    <div className="group relative flex cursor-pointer items-center gap-2 rounded-full border border-line-strong py-1 pl-1 pr-2.5 hover:bg-paper focus-within:bg-paper" tabIndex={0}>
      {user.photoURL ? (
        <img
          src={user.photoURL}
          alt=""
          referrerPolicy="no-referrer"
          className="h-7 w-7 rounded-full object-cover"
        />
      ) : (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink font-mono text-[10px] font-semibold text-white">
          {firstName[0] ?? "?"}
        </span>
      )}
      <span className="text-sm text-ink-soft">{firstName}</span>
      {/* Hover menu */}
      <div className="absolute right-0 top-full z-20 hidden pt-2 group-hover:block group-focus-within:block">
        <div className="w-56 rounded-[10px] border border-line bg-white p-4 shadow-lg">
          <div className="text-sm font-semibold text-ink">{user.displayName}</div>
          <div className="mt-0.5 truncate font-mono text-[11px] text-muted">{user.email}</div>
          <Link
            to="/template"
            className="btn-secondary mt-3 block w-full text-center text-xs sm:hidden"
          >
            View/Edit Template
          </Link>
          <Link
            to="/settings"
            className="btn-secondary mt-3 block w-full text-center text-xs"
          >
            Notification settings
          </Link>
          <button
            onClick={() => void signOut()}
            className="btn-secondary mt-2 w-full text-xs"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
